import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import Draggable from 'react-draggable';

import { useMedia, useObservable } from 'shared/hooks';
import { UiButton, UiIcon } from 'shared/uikit-new';
import { DateType } from 'shared/types';

import { Store } from 'pages/calendar/store';

import { CalendarPageMonthCalendarItem } from '../CalendarPageMonthCalendarItem';

import './styles.scss';
import { CalendarPageCreatingPopout } from 'pages/calendar/components/CalendarPageCreatingPopout';

interface PropsType {
    isOpened: boolean;
    onClose: () => void;
    date: DateType;
    bounds?: DOMRect | null;
}

export const CalendarPageDayPreview = observer(function CalendarPageDayPreview({ isOpened, onClose, bounds, date }: PropsType) {
    const ref = useRef<HTMLDivElement>(null);
    const store = useObservable({
        isLoading: true,
        left: 0,
        top: 0,
        zIndex: 0,
    });

    const { is320, is768 } = useMedia()

    useEffect(() => {
        if (!isOpened) {
            return;
        }
        store.set('zIndex', +`${Math.floor(Date.now() / 1000)}`.slice(-4));
    }, [isOpened, store]);

    useEffect(() => {
        if (!bounds) {
            return;
        }
        const width = 295;
        const height = 200;
        const fromLeft = bounds.x + bounds.width + 4;
        const fromRight = bounds.x - 4 - width;
        const fromTop = bounds.y;
        if(is320 || is768) {
            store.set('left', (window.innerWidth - width) / 2);
            store.set('top', window.innerHeight * 0.15);
        } else {
            store.set('left', window.innerWidth - fromLeft > width ? fromLeft : fromRight);
            store.set('top', window.innerHeight - fromTop > height ? fromTop : window.innerHeight - height);
        }
        store.set('zIndex', +`${Math.floor(Date.now() / 1000)}`.slice(-4));
    }, [store, bounds]);

    if (!isOpened) {
        return null;
    }

    const startAt = date.startOf('day');
    const endAt = date.endOf('day');
    const calendarItems = Store.calendarItems.filter(calendarItem => {
        return calendarItem.startAtDate.isAfter(startAt) && calendarItem.startAtDate.isBefore(endAt);
    });

    return (
        <Draggable
            nodeRef={ref}
            cancel={
                '.calendar-page-day-preview__day, .calendar-page-day-preview__name, .calendar-page-day-preview__close, .calendar-page-day-preview__content, .calendar-page-calendar-item-preview'
            }
        >
            <div className={'calendar-page-day-preview'} style={{ left: store.left, top: store.top, zIndex: store.zIndex }} ref={ref}>
                <CalendarPageCreatingPopout
                    className={'calendar-page-day-preview__inner'}
                    date={date.set('hours', 8).set('minutes', 0)}
                    render={({ onClick }) => (
                        <>
                            <div className='calendar-page-day-preview__header'>
                                <div
                                    className='calendar-page-day-preview__day'
                                    onClick={() => {
                                        Store.update({
                                            date: date,
                                            view: 'day',
                                        });
                                    }}
                                >
                                    {date.format('D')}
                                </div>
                                <div className='calendar-page-day-preview__name'>{date.format('dd')}</div>
                                <div className='calendar-page-day-preview__close' onClick={onClose}>
                                    <UiIcon.X size={24} />
                                </div>
                            </div>
                            {calendarItems.length > 0 && (
                                <div className='calendar-page-day-preview__content'>
                                    {calendarItems.map(calendarItem => (
                                        <CalendarPageMonthCalendarItem key={calendarItem.id} calendarItem={calendarItem} />
                                    ))}
                                </div>
                            )}
                            {calendarItems.length === 0 && (
                                <>
                                    <div className='calendar-page-day-preview__empty'>Активностей не запланировано</div>
                                    <UiButton className={'calendar-page-day-preview__create'} onClick={onClick}>
                                        <UiIcon.Plus size={20} />
                                        <span>Создать</span>
                                    </UiButton>
                                </>
                            )}
                        </>
                    )}
                />
            </div>
        </Draggable>
    );
});
